import { useOutletContext, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import Table, { Row, TableHeading, buildExpandableCountColumn } from "@/components/Table";
import NotesTable from "@/features/global/components/NotesTable";
import { toFormat, toUTCFormat } from "@/lib/dateHelpers";
import Tag from "@/components/Tag";
import MetricGrid from "@/components/MetricGrid";
import { Student } from "../types";
import { buildPATStatusForMetric } from "../_utils/patStatus";
import { REQUIRED_INTERNSHIP_MONTHS } from "../../../_shared/constants";
import type { Probation } from "../probations/types";
import EmptyState from "@/components/EmptyState";
import { buildActionColumn } from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import ProbationModal from "../probations/ProbationModal";
import { GET_UNIVERSITY_PROBATIONS_QUERY } from "../probations/queries";
import { useState, useCallback, useMemo } from "react";
import { PGMProgramTable } from "@/features/person/components/PGMProgramTable";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import { NoteNotableValues } from "@/types";
import useCapabilities from "@/hooks/useCapabilities";
import { getProbationTypeLabel } from "../probations/_constants";

export default function StudentPGMDetailsPage() {
  const { student } = useOutletContext<{ student: Student }>();
  const { universityId, studentId } = useParams();
  const { Menu, setMenu } = useTableRowMenu();

  const actualUniversityId = student?.university?.id;

  const { data: probationsData, refetch: refetchProbations } = useQuery(GET_UNIVERSITY_PROBATIONS_QUERY, {
    variables: { universityId: actualUniversityId || "", personId: student?.id, first: 100 },
    skip: !actualUniversityId || !student?.id
  });

  const activeProbations = useMemo(() => {
    if (!student || !probationsData?.university?.probations?.nodes) return [];

    return probationsData.university.probations.nodes
      .filter((p: Probation) => p.active && p.person.id === student.id)
      .sort((a: Probation, b: Probation) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [student, probationsData]);

  const probationHistory = useMemo(() => {
    if (!student || !probationsData?.university?.probations?.nodes) return [];

    const filtered = probationsData.university.probations.nodes.filter((p: Probation) => p.person.id === student.id);

    return filtered.sort(
      (a: Probation, b: Probation) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );
  }, [student, probationsData]);

  const {
    expandRowId: expandProbationId,
    highlightNoteId,
    tableRef: probationTableRef
  } = useNoteDeepLink({ dataReady: probationHistory.length > 0, expandParam: "expandProbationNotes" });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    mode: "create" | "end" | "edit";
    probation: Probation | null;
  }>({
    isOpen: false,
    mode: "create",
    probation: null
  });

  const handleOpenEndModal = useCallback((probation: Probation) => {
    setModalState({ isOpen: true, mode: "end", probation });
  }, []);

  const handleOpenEditModal = useCallback((probation: Probation) => {
    setModalState({ isOpen: true, mode: "edit", probation });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState({ isOpen: false, mode: "create", probation: null });
  }, []);

  const handleModalSuccess = useCallback(() => {
    refetchProbations();
    handleCloseModal();
  }, [refetchProbations, handleCloseModal]);

  const { id: currentUserId } = useCapabilities();
  const [addNoteProbationId, setAddNoteProbationId] = useState<string | null>(null);

  const handleOpenAddNote = useCallback((probation: Probation) => {
    setAddNoteProbationId(probation.id);
  }, []);

  const handleCloseAddNote = useCallback(() => {
    setAddNoteProbationId(null);
  }, []);

  const handleAddNoteSuccess = useCallback(() => {
    refetchProbations();
    setAddNoteProbationId(null);
  }, [refetchProbations]);

  const probationHistoryColumns = useMemo(
    () => [
      {
        accessorKey: "startedAt",
        header: "Start Date",
        cell: ({ row }: { row: Row<Probation> }) => toUTCFormat(row.original.startedAt),
        enableSorting: false,
        size: 150,
        minSize: 120
      },
      {
        accessorKey: "endedAt",
        header: "End Date",
        cell: ({ row }: { row: Row<Probation> }) => (row.original.endedAt ? toUTCFormat(row.original.endedAt) : "--"),
        enableSorting: false,
        size: 150,
        minSize: 120
      },
      {
        accessorKey: "probationType",
        header: "Type",
        cell: ({ row }: { row: Row<Probation> }) => getProbationTypeLabel(row.original.probationType),
        enableSorting: false,
        size: 180,
        minSize: 140
      },
      buildExpandableCountColumn<Probation>({
        accessorKey: "notes",
        header: "Notes",
        getCount: (row) => row.notes?.length || 0,
        size: 100,
        minSize: 80,
        autoExpandRowId: expandProbationId
      }),
      {
        accessorKey: "active",
        header: "Status",
        cell: ({ row }: { row: Row<Probation> }) => (
          <Tag tagType={row.original.active ? "error" : "surface"} size="small">
            {row.original.active ? "Active" : "Inactive"}
          </Tag>
        ),
        enableSorting: false,
        size: 80,
        minSize: 80,
        meta: {
          headerClassName: "hide-header-in-column"
        }
      },
      buildActionColumn<Probation>({
        getActions: (row) => {
          const actions: Action[] = [];

          if (row.active) {
            actions.push({
              section: "Manage",
              text: "End Probation",
              onClick: () => handleOpenEndModal(row)
            });

            actions.push({
              section: "Manage",
              text: "Edit Probation Type",
              onClick: () => handleOpenEditModal(row)
            });
          }

          actions.push({
            section: "Actions",
            text: "Add Note",
            onClick: () => handleOpenAddNote(row)
          });

          return actions;
        },
        actionId,
        setMenu
      })
    ],
    [setMenu, handleOpenEndModal, handleOpenEditModal, handleOpenAddNote, expandProbationId]
  );

  const activeProbationTooltip = useMemo(() => {
    if (activeProbations.length === 0) return undefined;

    if (activeProbations.length === 1) {
      const probation = activeProbations[0];
      const dateLabel = toUTCFormat(probation.startedAt);
      return {
        supportingText: `Student on Probation (${getProbationTypeLabel(probation.probationType)} — ${dateLabel})`
      };
    }

    return {
      supportingText: [
        `Student on Probation (${activeProbations.length} active)`,
        ...activeProbations.map(
          (probation) => `${getProbationTypeLabel(probation.probationType)} (${toUTCFormat(probation.startedAt)})`
        )
      ].join("\n")
    };
  }, [activeProbations]);

  if (!student) {
    return null;
  }

  return (
    <>
      <MetricGrid
        metrics={[
          {
            label: "Cohort",
            value:
              student.universityCohort?.name || (student.membershipSince ? toFormat(student.membershipSince) : "--"),
            iconName: "today",
            variant: activeProbations.length > 0 ? "error" : undefined,
            tooltip:
              activeProbationTooltip ??
              (student.universityCohort?.name && student.membershipSince
                ? {
                    supportingText: `Enrolled ${toFormat(student.membershipSince)}`
                  }
                : undefined)
          },
          (() => {
            const hasInProgressLevel = student.educationPgmProgramHistory.some((level) => !level.completed);
            const isComplete = !hasInProgressLevel;

            return {
              label: `PGM ${student.educationLatestPgmProgram.curriculumVersion} Level ${student.educationLatestPgmProgram.level}`,
              value: isComplete ? "Complete" : "In Progress",
              iconName: "school" as const,
              variant: isComplete ? "success" : "warn"
            };
          })(),
          (() => {
            const totalCredits = student.totalInternshipMonths || 0;
            const isComplete = totalCredits >= REQUIRED_INTERNSHIP_MONTHS;

            const formatMonths = (value: number) => (value % 1 === 0 ? value.toString() : value.toFixed(1));

            return {
              label: "Internship Months",
              value: `${formatMonths(totalCredits)} / ${formatMonths(REQUIRED_INTERNSHIP_MONTHS)}`,
              iconName: "business_center" as const,
              variant: isComplete ? "success" : "warn"
            };
          })(),
          (() => {
            const patStatus = buildPATStatusForMetric(student.prospectProgram?.playingAbilityTestStatus);
            return {
              label: "PAT Status",
              value: patStatus.value,
              iconName: "sports_golf",
              variant: patStatus.variant,
              link: {
                to: "/education/universities/:universityId/students/:studentId/pat-results" as const,
                params: { universityId: universityId!, studentId: studentId! }
              }
            };
          })()
        ]}
        layout="expanded"
      />

      <PGMProgramTable
        key={`pgm-table-${student.pgaId}`}
        personId={student.pgaId}
        tableHeading="PGM Program Progress"
        tableBody={`Program Type: ${student.educationLatestPgmProgram.programType}`}
        testId="program-progress-table"
        showMetrics={false}
        context="student"
      />

      <div ref={probationTableRef}>
        <TableHeading
          heading="Probation History"
          body="View probations for this student, including active and ended probations."
        />
        <Table
          data={probationHistory || []}
          columns={probationHistoryColumns}
          loading={false}
          data-testid="probation-history-table"
          renderExpandedRow={({ row }) => (
            <NotesTable
              variant="details-panel"
              data={row.original.notes}
              title={`Notes (${row.original.notes.length})`}
              highlightNoteId={highlightNoteId}
              noteEditing={{
                enabled: true,
                notableId: row.original.id,
                notableType: NoteNotableValues.UNIVERSITY_PROBATION,
                refetchQueries: [GET_UNIVERSITY_PROBATIONS_QUERY],
                pgaId: student?.pgaId
              }}
            />
          )}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Probation History"
              caption="This student has not been placed on probation."
              iconName="person_check"
            />
          )}
        />
      </div>

      <Menu />

      {actualUniversityId && (
        <ProbationModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
          universityLegacyId={universityId || ""}
          mode={modalState.mode}
          probation={modalState.probation}
          selectedStudent={null}
        />
      )}

      {addNoteProbationId && (
        <AddEditNoteSideSheet
          open={!!addNoteProbationId}
          onClose={handleCloseAddNote}
          notableId={addNoteProbationId}
          notableType={NoteNotableValues.UNIVERSITY_PROBATION}
          refetchQueries={[GET_UNIVERSITY_PROBATIONS_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={student?.pgaId}
          objectType="student"
          onSaveSuccess={handleAddNoteSuccess}
        />
      )}
    </>
  );
}
